import { defineStore } from "pinia";
import _ from "lodash";
import {
    FetchState,
    HsabExpenseCategory,
    ManagedExpenseFilterType,
    ManagedExpenseInitialState,
    ManagedExpensePagedResponse,
    ManagedExpensePageRequest,
    ManagedExpensesSortableColumns,
    SortOrder
} from "@/shared/types";
import * as expenseService from "@/shared/api/expenseService";
import * as storeHelper from "@/shared/storeHelper";
import Vue from "vue";
import moment from "moment";
import { openUrl } from "@/shared/urlHelper";

export const initialState: ManagedExpenseInitialState = {
    currentPage: {
        fetchState: FetchState.Uninitialized,
        page: 1,
        pageSize: 10,
        baseFilter: ManagedExpenseFilterType.MyExpenses,
        textSearch: null,
        categoryFilter: null,
        startDateFilter: null,
        endDateFilter: null,
        dateFilterDisplayText: null,
        sortByColumn: ManagedExpensesSortableColumns.Date,
        sortOrder: SortOrder.Descending,
        totalRecords: 0,
        numberOfPages: null,
        records: []
    },
    showAdditionalFilters: false,
    filterOptions: {
        categories: [],
        years: [],
    },
    actionNeededPreview: {
        fetchState: FetchState.Uninitialized,
        recordsPreview: [],
        totalRecords: null,
    },
    userHasHealthPlanExpense: false,
    userHasHealthPlanExpenseFetchState: FetchState.Uninitialized
};

export const useManagedExpenseStore = defineStore("managedExpense", {
    state: (): ManagedExpenseInitialState => (_.cloneDeep(initialState)),
    getters: {
        isCurrentPageLoaded: state => state.currentPage.fetchState === FetchState.Loaded,
        isCurrentPageLoading: state => state.currentPage.fetchState === FetchState.Loading,
        isCurrentPageLoadFailed: state => state.currentPage.fetchState === FetchState.Failed,
        currentPageItems: state => state.currentPage.records,
        currentPageNumber: state => state.currentPage.page,
        currentBaseFilter: state => state.currentPage.baseFilter,
        totalItemCount: state => state.currentPage.totalRecords,
        totalPageCount: state => state.currentPage.numberOfPages !== null ? state.currentPage.numberOfPages : 0,
        hasActionNeeded: state => state.actionNeededPreview.totalRecords !== null && state.actionNeededPreview.totalRecords >= 1,
        actionNeededItems: state => state.actionNeededPreview.recordsPreview,
    },
    actions: {
        overwriteRecordsForPage (response: ManagedExpensePagedResponse) {
            // overwrite the existing records in the array so that the UI does not jump
            // from 10 records to 0 and back to 10 when paging

            for (let index = this.currentPage.pageSize - 1; index >= 0; index--) {
                if (index < response.records.length) {
                    Vue.set(this.currentPage.records, index, response.records[index]);
                } else {
                    Vue.delete(this.currentPage.records, index);
                }
            }
        },
        reloadCurrentPage () {
            return this.loadManagedExpensesByPage(this.currentPage.page, this.currentPage.baseFilter);
        },
        loadManagedExpensesByPage (page: number, filter: ManagedExpenseFilterType = undefined, args?: { username: string, partnerId: number }) {
            if (filter) {
                this.currentPage.baseFilter = filter;
            }
            if (this.currentPage.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.currentPage.fetchState = FetchState.Loading;

            const request: ManagedExpensePageRequest = {
                pageNumber: page,
                pageSize: this.currentPage.pageSize,
                baseFilter: this.currentPage.baseFilter,
                textSearch: this.currentPage.textSearch,
                categoryFilter: this.currentPage.categoryFilter,
                startDateFilter: this.currentPage.startDateFilter,
                endDateFilter: this.currentPage.endDateFilter,
                sortByColumn: this.currentPage.sortByColumn,
                sortOrder: this.currentPage.sortOrder,
            };
            return expenseService.getManagedExpensesPage(request, args)
                .then(response => {
                    this.currentPage.page = page;
                    this.currentPage.totalRecords = response.data.totalRecords;
                    this.currentPage.numberOfPages = response.data.numberOfPages;
                    this.overwriteRecordsForPage(response.data);
                    this.filterOptions = response.data.filterOptions;
                    this.currentPage.fetchState = FetchState.Loaded;
                })
                .catch(err => {
                    this.currentPage.fetchState = FetchState.Failed;
                    storeHelper.handleError({ error: err, rethrow: true, timeout: 10000 });
                });
        },
        loadActionNeededPreview (recordsToLoad = 4, force = false) {
            if (!force && (this.actionNeededPreview.fetchState === FetchState.Loading || this.actionNeededPreview.fetchState === FetchState.Loaded)) {
                return Promise.resolve();
            }

            this.actionNeededPreview.fetchState = FetchState.Loading;

            const request: ManagedExpensePageRequest = {
                pageNumber: 1,
                pageSize: recordsToLoad,
                baseFilter: ManagedExpenseFilterType.ActionNeeded,
                textSearch: null,
                categoryFilter: null,
                startDateFilter: null,
                endDateFilter: null,
                sortByColumn: ManagedExpensesSortableColumns.Date,
                sortOrder: SortOrder.Descending,
            };
            return expenseService.getManagedExpensesPage(request)
                .then(response => {
                    this.actionNeededPreview.totalRecords = response.data.totalRecords;
                    this.actionNeededPreview.numberOfPages = response.data.numberOfPages;
                    this.actionNeededPreview.recordsPreview = response.data.records;
                    this.actionNeededPreview.fetchState = FetchState.Loaded;
                })
                .catch(err => {
                    this.actionNeededPreview.fetchState = FetchState.Failed;
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        setBaseFilterType (filter: ManagedExpenseFilterType) {
            if (this.currentPage.baseFilter === filter) {
                return;
            }

            this.currentPage.baseFilter = filter;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1);
        },
        setCategoryFilter (category: HsabExpenseCategory) {
            if (this.currentPage.categoryFilter === category) {
                return;
            }
            this.currentPage.categoryFilter = category;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1);
        },
        setDateRangeFilter (dateRangeFilter: string) {
            if (dateRangeFilter === null) {
                this.currentPage.startDateFilter = null;
                this.currentPage.endDateFilter = null;
            } else if (dateRangeFilter === "last12") {
                this.currentPage.startDateFilter = moment().add(-1, "y").format("MM/DD/YYYY");
                this.currentPage.endDateFilter = null;
            } else {
                this.currentPage.startDateFilter = "01/01/" + dateRangeFilter;
                this.currentPage.endDateFilter = "12/31/" + dateRangeFilter;
            }
            this.currentPage.dateFilterDisplayText = dateRangeFilter;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1);
        },
        setTextSearch (text: string) {
            if (this.currentPage.textSearch === text) {
                return;
            }
            this.currentPage.textSearch = text;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1);
        },
        setSortColumn (col: ManagedExpensesSortableColumns) {
            if (this.currentPage.sortByColumn === col) {
                this.reverserSortOrder();
            }
            this.currentPage.sortByColumn = col;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1);
        },
        setSortOrder (order: SortOrder) {
            if (order === SortOrder.None) {
                order = SortOrder.Descending;
            }
            if (this.currentPage.sortOrder === order) {
                return;
            }
            this.currentPage.sortOrder = order;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1);
        },
        toggleShowAdditionalFilters () {
            this.showAdditionalFilters = !this.showAdditionalFilters;
        },
        resetFilters () {
            this.showAdditionalFilters = false;
            this.currentPage.textSearch = null;
            this.currentPage.categoryFilter = null;
            this.currentPage.startDateFilter = null;
            this.currentPage.endDateFilter = null;
            this.currentPage.dateFilterDisplayText = null;
            this.currentPage.sortByColumn = ManagedExpensesSortableColumns.Date;
            this.currentPage.sortOrder = SortOrder.Descending;
        },
        reverserSortOrder () {
            switch (this.currentPage.sortOrder) {
                case SortOrder.None:
                    this.setSortOrder(SortOrder.Descending);
                    break;
                case SortOrder.Ascending:
                    this.setSortOrder(SortOrder.Descending);
                    break;
                case SortOrder.Descending:
                    this.setSortOrder(SortOrder.Ascending);
                    break;

            }
        },
        setSortByColumnAndSortOrder (col: ManagedExpensesSortableColumns, order: SortOrder) {
            if (this.currentPage.sortByColumn === col && this.currentPage.sortOrder === order) {
                return;
            }

            this.currentPage.sortByColumn = col;
            this.currentPage.sortOrder = order;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1);
        },
        setSortByColumnAndSortOrderAndFilter (
            col: ManagedExpensesSortableColumns,
            order: SortOrder,
            filter: ManagedExpenseFilterType = undefined,
            args?: { username: string, partnerId: number }) {
            if (this.currentPage.sortByColumn === col
                && this.currentPage.sortOrder === order
                && this.currentPage.baseFilter === filter) {
                return;
            }
            this.currentPage.sortByColumn = col;
            this.currentPage.sortOrder = order;
            this.currentPage.baseFilter = filter;
            this.currentPage.fetchState = FetchState.Uninitialized;
            this.loadManagedExpensesByPage(1, filter, args);
        },
        exportAllManagedExpenses () {
            const request: ManagedExpensePageRequest = {
                baseFilter: ManagedExpenseFilterType.None,
                textSearch: null,
                categoryFilter: null,
                startDateFilter: null,
                endDateFilter: null,
                sortByColumn: ManagedExpensesSortableColumns.Date,
                sortOrder: SortOrder.Ascending,
            };
            this.exportManagedExpenses(request);
        },
        exportCurrentlyFilteredManagedExpenses () {
            const request: ManagedExpensePageRequest = {
                baseFilter: this.currentPage.baseFilter,
                textSearch: this.currentPage.textSearch,
                categoryFilter: this.currentPage.categoryFilter,
                startDateFilter: this.currentPage.startDateFilter,
                endDateFilter: this.currentPage.endDateFilter,
                sortByColumn: this.currentPage.sortByColumn,
                sortOrder: this.currentPage.sortOrder,
            };
            this.exportManagedExpenses(request);
        },
        exportManagedExpenses (request: ManagedExpensePageRequest) {
            return expenseService.exportManagedExpensesUrl(request)
                .then(response => {
                    openUrl(response.data);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadUserHasHealthPlanExpense () {
            if (this.userHasHealthPlanExpenseFetchState === FetchState.Loading || this.userHasHealthPlanExpenseFetchState === FetchState.Loaded) {
                return Promise.resolve(this.userHasHealthPlanExpense);
            }

            this.userHasHealthPlanExpenseFetchState = FetchState.Loading;

            return expenseService.getUserHasHealthPlanExpense()
                .then(response => {
                    this.userHasHealthPlanExpense = response.data;
                    this.userHasHealthPlanExpenseFetchState = FetchState.Loaded;
                })
                .catch(err => {
                    this.userHasHealthPlanExpenseFetchState = FetchState.Failed;
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});